import React, { useEffect } from 'react'

import { IntegrationEmail, IntegrationEmailThread } from '../../../models/Email'
import { useLastContactEmailFromThread } from '../Table/hooks'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { EmailResponseInitialization } from '../Initialization/EmailResponseInitialization'
import { EmailResponsePoller } from '../Poller/EmailResponsePoller'
import {
  selectEmailResponseExecutionLoading,
  selectEmailResponseLoading,
  selectEmailResponsePollingEnabled,
  selectShouldPollEmailResponse,
  startEmailResponsePolling,
  stopEmailResponsePolling,
} from '../../../store/emailHub/emailResponse'
import {
  selectEmailMetadataPollingEnabled,
  selectShouldPollMetadata,
  startEmailMetadataPolling,
  stopEmailMetadataPolling,
} from '../../../store/emailHub/emailMetadata'
import { EmailResponse } from './EmailResponse'
import { ExternalUserServiceIntegration, User } from '../../../models/User'
import { EmailMetadata } from '../../../models/EmailMetadata'
import { EmailResponseProcessing } from '../Common/EmailResponseProcessing'
import { useEmailContactByEmail } from '../hooks'

export interface EmailResponseViewProps {
  thread: IntegrationEmailThread
}

export const EmailResponseView: React.FC<EmailResponseViewProps> = ({ thread }) => {
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  const { lastEmailByContact, lastEmailByContactMetadata } = useLastContactEmailFromThread(thread)
  const lastEmailAnswered =
    emailIntegration &&
    lastEmailByContact &&
    thread.emails.some((email) => email.sender.email === emailIntegration.email && email.createdAt > lastEmailByContact.createdAt)

  if (!emailIntegration || !lastEmailByContact) {
    return null
  }

  if (lastEmailAnswered) {
    return <EmailResponseProcessing emailConnector={emailIntegration.serviceId} email={lastEmailByContact} metadata={lastEmailByContactMetadata} />
  }

  return <EmailResponseBody email={lastEmailByContact} metadata={lastEmailByContactMetadata} />
}

interface EmailResponseBodyProps {
  email: IntegrationEmail
  metadata: EmailMetadata | null
}

const EmailResponseBody: React.FC<EmailResponseBodyProps> = ({ email, metadata }) => {
  const dispatch = useAppDispatch()

  const shouldPollMetadata = useAppSelector((state) => selectShouldPollMetadata(state, email.externalId))
  const shouldPollEmailResponse = useAppSelector((state) => selectShouldPollEmailResponse(state, email.externalId))

  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const { contact: resolvedContact, isFetching: senderContactLoading } = useEmailContactByEmail(email.sender.email)

  useEffect(() => {
    if (shouldPollEmailResponse) {
      dispatch(startEmailResponsePolling())
    } else {
      dispatch(stopEmailResponsePolling())
    }
  }, [dispatch, shouldPollEmailResponse])

  useEffect(() => {
    if (shouldPollMetadata) {
      dispatch(startEmailMetadataPolling())
    } else {
      dispatch(stopEmailMetadataPolling())
    }
  }, [dispatch, shouldPollMetadata])

  if (!emailIntegration) {
    return null
  }

  if (!resolvedContact) {
    return senderContactLoading ? <div className='skeleton mt-2' style={{ width: '100%', height: '120px' }} /> : null
  }

  return (
    <>
      <EmailResponseInitialization emailId={email.externalId} />
      <EmailResponsePoller emailId={email.externalId} />
      <>
        <EmailResponseContent email={email} metadata={metadata} contact={resolvedContact} emailIntegration={emailIntegration} />
      </>
    </>
  )
}

interface EmailResponseContentProps {
  email: IntegrationEmail
  metadata: EmailMetadata | null
  contact: User
  emailIntegration: ExternalUserServiceIntegration
}

const EmailResponseContent: React.FC<EmailResponseContentProps> = ({ email, metadata, contact, emailIntegration }) => {
  const metadataIsPolling = useAppSelector(selectEmailMetadataPollingEnabled)
  const emailResponseExecuteLoading = useAppSelector(selectEmailResponseExecutionLoading)
  const emailResponseIsPolling = useAppSelector(selectEmailResponsePollingEnabled)
  const emailResponseLoading = useAppSelector(selectEmailResponseLoading)

  const emailResponseGenerating = metadataIsPolling || emailResponseIsPolling || emailResponseExecuteLoading

  if (emailResponseLoading) {
    return <div className='skeleton mt-2' style={{ width: '100%', height: '300px' }} />
  }

  if (emailResponseGenerating) {
    return (
      <div className='d-flex w-100 p-3 align-items-center justify-content-center'>
        <div className='skeleton' style={{ width: '100%', height: '300px' }}>
          <div className='d-flex flex-column align-items-center justify-content-center h-100'>
            <div>Generating response…</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-3'>
      <div className='d-flex flex-column gap-3 p-3 zocks-border-1.0 border'>
        <EmailResponse emailConnector={emailIntegration.serviceId} contact={contact} email={email} metadata={metadata} />
      </div>
    </div>
  )
}
